import AuthenticationService from "@services/AuthenticationService";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {useCallback, useContext, useState} from "react";
import {NotificationsContext} from "@ui-components/Notifications";
import {useIntl} from "react-intl";
import CountryConfigContext from "../../../../CountryConfigContext";
import useAsyncError from "@hooks/useAsyncError";
import {ServicesType, useSurveyProducts} from "../../../commons";
import {PDFGenerationPayloadSchema} from "../types";
import {
  buildWorksiteNotePdfObj,
  buildWorksitePdfObj,
  getSurveyData,
  buildPdfImages,
  buildPdfProducts
} from "../lib/utilities";
import {
  ServiceSurveysWithIdSchema,
  UserWithRoleSchema,
  WorksitesNotesWithIdSchema,
  WorksitesWithIdSchema
} from "../../../types";
import useGetServiceConfiguration from "@hooks/useGetServiceConfiguration";
import {useWorksiteNotes} from "./useWorksiteNotes";
import {downloadBlob} from "@feature/commons/lib/utils";
import {LogoSchema} from "@feature/profile-logo-editor/types";
import {useLogo} from "@feature/profile-logo-editor/hooks/useLogo";
import {useCrossServicesConfigurations} from "@feature/cross_services_configurations/hooks/useCrossServicesConfigurations";


type Handler<T> = (
  type_: ServicesType,
  worksite: WorksitesWithIdSchema,
  worksiteUsers: UserWithRoleSchema[],
  surveys: ServiceSurveysWithIdSchema[],
  title: string | null,
  description: string | null,
  includeNotes: boolean,
  imagesUrls: string[],
  coverImageUrl: string | null,
  targetPrice: "kerakoll" | "tintometer",
  hasAttachments?: boolean,
) => Promise<T>;


export const useDownloadPdf = () => {

  // context
  const {
    config: {products, systems, services},
  } = useContext(CountryConfigContext);
  const {push} = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();
  const {fetch: fetchSurveysProducts} = useSurveyProducts(true);
  const {fetch: fetchWorksiteNotes} = useWorksiteNotes();
  const getServiceConfiguration = useGetServiceConfiguration(false);
  const {getCrossServicesConfigurations} = useCrossServicesConfigurations();
  const {getLogo} = useLogo();

  // state
  const [loading, setLoading] = useState(false);

  // utilities
  const {
    role_type: currenUserRole
  } = AuthenticationService.getUserData();

  const fetch: Handler<Blob | null> = useCallback(
    async (...params) => {
      const [
        type_,
        worksite,
        worksiteUsers,
        surveys,
        title,
        description,
        includeNotes,
        imagesUrls,
        coverImageUrl,
        targetPrice,
      ] = params;

      setLoading(true);
      try {

        // Download needed data for thye PDF constructions
        const [surveysProducts, worksiteNotes, logo] = await Promise.all([
          // in order to make evaluate with proper context (ticket H2800382902)
          fetchSurveysProducts(surveys, null) as Promise<any>,
          (includeNotes
            ? fetchWorksiteNotes(worksite.id)
            : new Promise<WorksitesNotesWithIdSchema[]>((resolve) => {
              return resolve([]);
            })
          ) as Promise<WorksitesNotesWithIdSchema[]>,
          getLogo() as unknown as Promise<LogoSchema>,
        ]);
        // Define the payload
        const payload = {
          type_,
          worksite: buildWorksitePdfObj(intl, worksite, worksiteUsers),
          title,
          description,
          cover_image_url: coverImageUrl,
          data: await getSurveyData(
            intl,
            apiRequest!,
            surveys,
            services,
            products,
            systems,
            currenUserRole,
            getServiceConfiguration,
            getCrossServicesConfigurations,
            surveysProducts,
            targetPrice,
          ),
          products: buildPdfProducts(surveysProducts),
          images: buildPdfImages(
            (worksite.images_urls as any)?.data,
            imagesUrls,
          ),
          notes: worksiteNotes.map(buildWorksiteNotePdfObj),
          ...(logo ? {logo_url: logo.url} : {}),
        } as PDFGenerationPayloadSchema;
        // Send request and return the blob
        return await apiRequest!.post("/worksites/pdf", payload,{responseType: "blob"}) as Blob;
      }
      catch (error) {
        push({type: "error", title: intl.formatMessage({id: "server_error"})});
        throwError(error);
        return null;
      }
      finally {
        setLoading(false);
      }
    },
    [],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const download: Handler<void> = useCallback(
    async (...params) => {
      const [_, worksite] = params;
      const blob = await fetch(...params);
      if (!blob) return;
      const filename = !!worksite?.des_worksite
        ? `REPORT ${worksite.des_worksite.toUpperCase()}.pdf`
        : "report.pdf";
      downloadBlob(blob, filename);
    },
    [],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  const open: Handler<void> = useCallback(
    async (...params) => {
      const blob = await fetch(...params);
      if (!blob) return;
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    },
    [],  // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {fetch, download, open, loading, setLoading};
};